if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.lastThumbnailActivationAt = 0;
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 150));
          // NOTE: Mobile horizontal swipe is handled natively by the browser
          // via CSS (touch-action: pan-x pan-y + overflow-x: auto on
          // .product__media-list in main-product.liquid). We removed the
          // previous custom JS drag handler because it was fighting native
          // scroll — users reported that the carousel wouldn't respond to
          // finger swipes at all.
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const btn = mediaToSwitch.querySelector('button');
            if (!btn) return;
            const activateFromThumb = (event) => {
              // On mobile, touch interaction can emit both touchend and click.
              // De-duplicate to ensure one reliable main-image switch.
              const now = Date.now();
              if (now - this.lastThumbnailActivationAt < 250) return;
              this.lastThumbnailActivationAt = now;
              event.preventDefault();
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
            };
            btn.addEventListener('click', activateFromThumb);
            btn.addEventListener('touchend', activateFromThumb, { passive: false });

            // Hover-to-preview: swap main image on mouseenter (desktop only)
            // Pass skipScroll=true so the page doesn't jump on hover
            mediaToSwitch.addEventListener('mouseenter', () => {
              if (this.mql.matches) {
                this.setActiveMedia(mediaToSwitch.dataset.target, false, true);
              }
            });
          });
        }

        const layout = this.dataset.desktopLayout || '';
        if (layout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const current = event.detail && event.detail.currentElement;
        if (!current || !current.dataset || !current.dataset.mediaId) return;
        const mediaId = current.dataset.mediaId;
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }
        if (this.elements.viewer) {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => {
            el.classList.toggle('is-active', el.dataset.mediaId === mediaId);
          });
        }
      }

      setActiveMedia(mediaId, prepend, skipScroll = false) {
        if (!this.elements.viewer) return;
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        // Skip scroll and sticky header logic for hover preview to prevent page jumps
        if (!skipScroll) {
          this.preventStickyHeader();
          window.setTimeout(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              // Only move the gallery's internal horizontal slider to the
              // active slide. Do NOT scroll the window vertically — clicking
              // a thumbnail should never yank the page up to the main image.
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
            }
          });
        }

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (!skipScroll && activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        const thumbBtn = thumbnail.querySelector('button');
        if (!thumbBtn) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbBtn.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer || !this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
